import { parser } from "../../erd-editor-schema/dist/v3/parser/index.js";
import { toJson } from "../../erd-editor-schema/dist/parser.js";
import { query } from "../../erd-editor-schema/dist/query/index.js";
import { ColumnOption, ColumnUIKey, OrderType, RelationshipType } from "../../constants/schema.js";
import { DatabaseVendor, DatabaseVendorToDatabase } from "../../constants/sql/database.js";
import { textInRange } from "../validation.js";
import { createTable } from "../collection/table.entity.js";
import { createColumn } from "../collection/tableColumn.entity.js";
import { bHas } from "../bit.js";
import { createIndex } from "../collection/index.entity.js";
import { createIndexColumn } from "../collection/indexColumn.entity.js";
import { createRelationship } from "../collection/relationship.entity.js";
import { toReferentialAction } from "../referentialAction.js";
import { findByName } from "./utils.js";
import { SortType, StatementType } from "../../schema-sql-parser/dist/parser/statement/index.js";
import { schemaSQLParser } from "../../schema-sql-parser/dist/parser/index.js";
//#region src/utils/schema-sql-parser/index.ts
function schemaSQLParserToSchemaJson(sql, ctx, prepare, database) {
	const schema = parser({});
	const vendor = Object.values(DatabaseVendor).find((value) => DatabaseVendorToDatabase[value] === database);
	const tables = mergeTables(getStatementMap(schemaSQLParser(sql, { database: vendor })));
	tables.forEach((table) => convertTable(schema, table, ctx));
	convertRelationship(schema, tables);
	convertIndex(schema, tables);
	return toJson(prepare ? prepare(schema) : schema);
}
function getStatementMap(statements) {
	const map = {
		tables: [],
		indexes: [],
		primaryKeys: [],
		foreignKeys: [],
		uniques: [],
		defaults: [],
		autoIncrements: [],
		tableComments: [],
		columnComments: []
	};
	for (const statement of statements) switch (statement.type) {
		case StatementType.createTable:
			if (statement.name) map.tables.push(statement);
			break;
		case StatementType.createIndex:
			if (statement.tableName && statement.columns.length) map.indexes.push(statement);
			break;
		case StatementType.alterTableAddPrimaryKey:
			if (statement.name && statement.columnNames.length) map.primaryKeys.push(statement);
			break;
		case StatementType.alterTableAddForeignKey:
			if (statement.name && statement.columnNames.length && statement.refTableName && (!statement.refColumnNames.length || statement.columnNames.length === statement.refColumnNames.length)) map.foreignKeys.push(statement);
			break;
		case StatementType.alterTableAddUnique:
			if (statement.name && statement.columns.length) map.uniques.push(statement);
			break;
		case StatementType.alterTableAddDefault:
			if (statement.name && statement.columnName && statement.default) map.defaults.push(statement);
			break;
		case StatementType.alterTableAlterColumnAutoIncrement:
			if (statement.name && statement.columnName) map.autoIncrements.push(statement);
			break;
		case StatementType.commentOnTable:
			if (statement.name) map.tableComments.push(statement);
			break;
		case StatementType.commentOnColumn: if (statement.tableName && statement.columnName) map.columnComments.push(statement);
	}
	return map;
}
function mergeTables({ tables, indexes, primaryKeys, foreignKeys, uniques, defaults, autoIncrements, tableComments, columnComments }) {
	indexes.forEach((index) => {
		const table = findByName(tables, index.tableName);
		if (!table) return;
		const created = keyOf(index);
		const sameIndex = table.indexes.find((other) => mayRepeat(table, other) && isKeyIndex(keyOf(other), created));
		if (sameIndex) {
			sameIndex.name ||= index.name;
			sameIndex.unique ||= index.unique;
			return;
		}
		if (table.keys.some((key) => isKeyIndex(key, created))) return;
		table.indexes.push({
			name: index.name,
			unique: index.unique,
			columns: index.columns
		});
	});
	primaryKeys.forEach((primaryKey) => {
		const table = findByName(tables, primaryKey.name);
		if (!table) return;
		const keyIndex = findKeyIndex(table, {
			name: primaryKey.usingIndexName || primaryKey.constraintName,
			columnNames: primaryKey.columnNames
		});
		if (keyIndex) table.indexes.splice(table.indexes.indexOf(keyIndex), 1);
		primaryKey.columnNames.forEach((columnName) => {
			const column = findByName(table.columns, columnName);
			if (!column) return;
			column.primaryKey = true;
		});
	});
	uniques.forEach((unique) => {
		const table = findByName(tables, unique.name);
		if (!table) return;
		const keyIndex = findKeyIndex(table, {
			name: unique.usingIndexName || unique.constraintName,
			columnNames: namesOf(unique.columns)
		});
		if (keyIndex && unique.columns.length > 1) {
			keyIndex.unique = true;
			return;
		}
		if (keyIndex) table.indexes.splice(table.indexes.indexOf(keyIndex), 1);
		if (unique.columns.length > 1) {
			table.indexes.push({
				name: unique.constraintName,
				unique: true,
				columns: unique.columns
			});
			return;
		}
		unique.columns.forEach(({ name }) => {
			const column = findByName(table.columns, name);
			if (!column) return;
			column.unique = true;
		});
	});
	foreignKeys.forEach((foreignKey) => {
		const table = findByName(tables, foreignKey.name);
		if (!table) return;
		table.foreignKeys.push({
			columnNames: foreignKey.columnNames,
			refTableName: foreignKey.refTableName,
			refColumnNames: foreignKey.refColumnNames,
			onDelete: foreignKey.onDelete,
			onUpdate: foreignKey.onUpdate
		});
	});
	defaults.forEach(({ name, columnName, default: value }) => {
		const table = findByName(tables, name);
		if (!table) return;
		const column = findByName(table.columns, columnName);
		if (!column) return;
		column.default = value;
	});
	autoIncrements.forEach(({ name, columnName }) => {
		const table = findByName(tables, name);
		if (!table) return;
		const column = findByName(table.columns, columnName);
		if (!column) return;
		column.autoIncrement = true;
		column.default = "";
	});
	tableComments.forEach(({ name, comment }) => {
		const table = findCommentTable(tables, name);
		if (!table) return;
		table.comment = comment;
	});
	columnComments.forEach(({ tableName, columnName, comment }) => {
		const table = findCommentTable(tables, tableName);
		if (!table) return;
		const column = findByName(table.columns, columnName);
		if (!column) return;
		column.comment = comment;
	});
	return tables;
}
function findCommentTable(tables, name) {
	return findByName(tables, name) ?? findByName(tables, name.slice(name.lastIndexOf(".") + 1));
}
function mayRepeat(table, index) {
	return index.name !== "" || table.keys.some((key) => isKeyIndex(key, keyOf(index)));
}
function findKeyIndex(table, key) {
	return table.indexes.find((index) => isKeyIndex(key, keyOf(index))) ?? null;
}
function isKeyIndex(key, index) {
	return key.name ? isSameName(key.name, index.name) && hasSameColumns(key.columnNames, index.columnNames) : hasColumnsInOrder(key.columnNames, index.columnNames);
}
var keyOf = ({ name, columns }) => ({
	name,
	columnNames: namesOf(columns)
});
var namesOf = (columns) => columns.map(({ name }) => name);
var isSameName = (a, b) => a.toUpperCase() === b.toUpperCase();
function hasSameColumns(a, b) {
	const names = new Set(a.map((name) => name.toUpperCase()));
	return a.length === b.length && b.every((name) => names.has(name.toUpperCase()));
}
function hasColumnsInOrder(a, b) {
	return a.length === b.length && a.every((name, i) => isSameName(name, b[i]));
}
function convertTable({ doc, collections }, table, { toWidth }) {
	const newTable = createTable({
		name: table.name,
		comment: table.comment,
		ui: {
			widthName: textInRange(toWidth(table.name)),
			widthComment: textInRange(toWidth(table.comment))
		}
	});
	table.columns.forEach((column) => {
		const newColumn = createColumn({
			tableId: newTable.id,
			name: column.name,
			comment: column.comment,
			dataType: column.dataType,
			default: column.default,
			options: (column.autoIncrement ? ColumnOption.autoIncrement : 0) | (column.primaryKey ? ColumnOption.primaryKey : 0) | (column.unique ? ColumnOption.unique : 0) | (column.nullable ? 0 : ColumnOption.notNull),
			ui: {
				widthName: textInRange(toWidth(column.name)),
				widthComment: textInRange(toWidth(column.comment)),
				widthDataType: textInRange(toWidth(column.dataType)),
				widthDefault: textInRange(toWidth(column.default)),
				keys: column.primaryKey ? ColumnUIKey.primaryKey : 0
			}
		});
		newTable.columnIds.push(newColumn.id);
		newTable.seqColumnIds.push(newColumn.id);
		query(collections).collection("tableColumnEntities").setOne(newColumn);
	});
	doc.tableIds.push(newTable.id);
	query(collections).collection("tableEntities").setOne(newTable);
}
function convertRelationship({ doc, collections }, tables) {
	const newTables = query(collections).collection("tableEntities").selectByIds(doc.tableIds);
	const columnCollection = query(collections).collection("tableColumnEntities");
	tables.forEach((table) => {
		if (!table.foreignKeys.length) return;
		const endTable = findByName(newTables, table.name);
		if (!endTable) return;
		const eColumns = columnCollection.selectByIds(endTable.columnIds);
		table.foreignKeys.forEach((foreignKey) => {
			const startTable = findByName(newTables, foreignKey.refTableName);
			if (!startTable) return;
			const sColumns = columnCollection.selectByIds(startTable.columnIds);
			const startColumns = [];
			const endColumns = [];
			if (foreignKey.refColumnNames.length) foreignKey.refColumnNames.forEach((refColumnName) => {
				const column = findByName(sColumns, refColumnName);
				if (!column) return;
				startColumns.push(column);
			});
			else {
				const primaryKeys = sColumns.filter((column) => bHas(column.ui.keys, ColumnUIKey.primaryKey));
				if (primaryKeys.length === 1) startColumns.push(...primaryKeys);
			}
			foreignKey.columnNames.forEach((columnName) => {
				const column = findByName(eColumns, columnName);
				if (!column) return;
				endColumns.push(column);
			});
			if (!startColumns.length || startColumns.length !== endColumns.length) return;
			endColumns.forEach((column) => {
				if (bHas(column.ui.keys, ColumnUIKey.primaryKey)) column.ui.keys |= ColumnUIKey.foreignKey;
				else column.ui.keys = ColumnUIKey.foreignKey;
			});
			const newRelationship = createRelationship({
				identification: !endColumns.some((column) => !(bHas(column.ui.keys, ColumnUIKey.primaryKey) && bHas(column.ui.keys, ColumnUIKey.foreignKey))),
				relationshipType: RelationshipType.ZeroN,
				onDelete: toReferentialAction(foreignKey.onDelete),
				onUpdate: toReferentialAction(foreignKey.onUpdate),
				start: {
					tableId: startTable.id,
					columnIds: startColumns.map((column) => column.id)
				},
				end: {
					tableId: endTable.id,
					columnIds: endColumns.map((column) => column.id)
				}
			});
			doc.relationshipIds.push(newRelationship.id);
			query(collections).collection("relationshipEntities").setOne(newRelationship);
		});
	});
}
function convertIndex({ doc, collections }, tables) {
	const newTables = query(collections).collection("tableEntities").selectByIds(doc.tableIds);
	tables.forEach((table) => {
		table.indexes.forEach((index) => {
			const targetTable = findByName(newTables, table.name);
			if (!targetTable) return;
			const columns = query(collections).collection("tableColumnEntities").selectByIds(targetTable.columnIds);
			const indexColumns = [];
			const newIndex = createIndex({
				name: index.name,
				tableId: targetTable.id,
				unique: index.unique
			});
			index.columns.forEach((column) => {
				const targetColumn = findByName(columns, column.name);
				if (!targetColumn) return;
				const newIndexColumn = createIndexColumn({
					indexId: newIndex.id,
					columnId: targetColumn.id,
					orderType: column.sort === SortType.asc ? OrderType.ASC : OrderType.DESC
				});
				indexColumns.push(newIndexColumn);
			});
			const complete = indexColumns.length === index.columns.length;
			if (indexColumns.length !== 0 && (complete || !index.unique)) {
				indexColumns.forEach((indexColumn) => {
					newIndex.indexColumnIds.push(indexColumn.id);
					newIndex.seqIndexColumnIds.push(indexColumn.id);
					query(collections).collection("indexColumnEntities").setOne(indexColumn);
				});
				doc.indexIds.push(newIndex.id);
				query(collections).collection("indexEntities").setOne(newIndex);
			}
		});
	});
}
//#endregion
export { schemaSQLParserToSchemaJson };
